import { Link } from "react-router";
import { Check, Minus } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Table, Td, Th } from "@/components/ui/Table";
import { Reveal } from "@/components/marketing/Reveal";
import { CtaBand, PageHero, Section, SectionIntro } from "@/components/marketing/Section";

/**
 * Pricing.
 *
 * ⚠ **No figures on this page, deliberately.** No price has been set for TaskIQ,
 * and a placeholder number on a public page is indistinguishable from a real one
 * to the person reading it - the first sales call then starts by contradicting
 * the website. What *is* settled is the packaging shape (per technician, per
 * month, with a genuinely free tier that the sign-up flow already provisions),
 * so that is what the page states.
 *
 * When a number exists, it goes in `TIERS[].price` and nowhere else.
 */

const TIERS = [
  {
    id: "pilot",
    name: "Pilot",
    price: "Free",
    unit: "for your first crew",
    lead: "Everything below, for a single site. What you get by signing up right now - no card, no sales call.",
    cta: { label: "Start free", to: "/signup", variant: "primary" as const },
    highlights: [
      "One site, up to five technicians",
      "Area scan, ladder angle and PPE check",
      "Full assessment history",
      "Email support",
    ],
  },
  {
    id: "crew",
    name: "Crew",
    price: "Per technician",
    unit: "billed monthly",
    lead: "For a company running TaskIQ across every crew, with the console as the record of who checked what.",
    cta: { label: "Talk to us", to: "/company#contact", variant: "primary" as const },
    featured: true,
    highlights: [
      "Unlimited sites and technicians",
      "Role-scoped console for supervisors and EHS",
      "Scheduled exports and webhooks",
      "Stop-work escalation and resolution",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    unit: "annual agreement",
    lead: "For a contractor reporting into several clients' compliance systems, with procurement and security review to get through.",
    cta: { label: "Talk to us", to: "/company#contact", variant: "secondary" as const },
    highlights: [
      "REST API and BI feed",
      "SSO through Microsoft Entra, Google or Apple",
      "Configurable per-role permissions",
      "Named contact and onboarding for your crews",
    ],
  },
];

/** `true` in every tier, `false` where it is genuinely absent. */
const MATRIX: {
  feature: string;
  note?: string;
  pilot: boolean;
  crew: boolean;
  enterprise: boolean;
}[] = [
  { feature: "Area scan", pilot: true, crew: true, enterprise: true },
  { feature: "Ladder angle check", pilot: true, crew: true, enterprise: true },
  { feature: "PPE check", pilot: true, crew: true, enterprise: true },
  { feature: "Has offline compatibility", pilot: true, crew: true, enterprise: true },
  { feature: "Timestamped, geotagged record", pilot: true, crew: true, enterprise: true },
  {
    feature: "Multiple sites",
    note: "Crews grouped by depot, region or client",
    pilot: false,
    crew: true,
    enterprise: true,
  },
  {
    feature: "Role-scoped console",
    note: "Field officer, admin and platform reach",
    pilot: false,
    crew: true,
    enterprise: true,
  },
  { feature: "Stop-work escalation", pilot: false, crew: true, enterprise: true },
  { feature: "Scheduled exports", pilot: false, crew: true, enterprise: true },
  { feature: "Webhooks", pilot: false, crew: true, enterprise: true },
  { feature: "REST API and BI feed", pilot: false, crew: false, enterprise: true },
  { feature: "Single sign-on", pilot: false, crew: false, enterprise: true },
  {
    feature: "Configurable permissions",
    note: "Per-role access levels you edit yourself",
    pilot: false,
    crew: false,
    enterprise: true,
  },
];

const FAQ = [
  {
    q: "Why per technician rather than per assessment?",
    a: "Because a per-assessment price is a reason not to run one. The whole product depends on a technician reaching for it on every job, including the routine ones, and any pricing that makes a check cost money will quietly stop the routine ones happening.",
  },
  {
    q: "Do we need to install anything on site?",
    a: "No. There are no cameras, no gateways and no network changes. TaskIQ runs on the phone the technician already carries, and the console is a web application.",
  },
  {
    q: "What happens with no coverage?",
    a: "Everything except the hazard list runs on the device, and the hazard list falls back to a cached model. The assessment is written locally and syncs when coverage returns - the technician sees “saved, will sync”, never a failure.",
  },
  {
    q: "Can we keep using our own compliance system?",
    a: "Yes, and most customers do. TaskIQ is standalone by default; webhooks, exports and the API are a layer on top. Connect one system, several, or none.",
  },
  {
    q: "Is any of this facial recognition?",
    a: "No, and it never will be. The PPE check detects equipment and has no concept of identity. This is a product commitment, not a current limitation.",
  },
];

export function Pricing() {
  return (
    <>
      <PageHero
        eyebrow="Pricing"
        title="Priced per technician, so nobody skips a check to save money."
        lead="Start free on one site today. When it is working, price it per technician per month - never per assessment, for the reason in the questions below."
      />

      {/* ── Tiers ─────────────────────────────────────────────────────────── */}
      <Section>
        <ul className="tier-grid">
          {TIERS.map((tier, index) => (
            <Reveal as="li" key={tier.id} delay={index * 70}>
              <Card tone={tier.featured ? "accent" : "surface"} padding="md" fill>
                <div className="tier__head">
                  <h2 className="tier__name">{tier.name}</h2>
                  {tier.featured && (
                    <Badge tone="accent" size="sm" uppercase>
                      Most crews
                    </Badge>
                  )}
                </div>

                <p className="tier__price">{tier.price}</p>
                <p className="tier__unit">{tier.unit}</p>

                <p className="tier__lead">{tier.lead}</p>

                <ul className="tier__highlights">
                  {tier.highlights.map((item) => (
                    <li key={item} className="tier__highlight">
                      <Check />
                      {item}
                    </li>
                  ))}
                </ul>

                <Link to={tier.cta.to} className="tier__cta">
                  <Button variant={tier.cta.variant} size="lg" block>
                    {tier.cta.label}
                  </Button>
                </Link>
              </Card>
            </Reveal>
          ))}
        </ul>
      </Section>

      {/* ── Comparison ────────────────────────────────────────────────────── */}
      <Section tone="sunken">
        <SectionIntro eyebrow="Compare" title="What is in each" />

        <Reveal className="pricing__compare">
          <Table
            minWidth="lg"
            head={
              <>
                <Th>Feature</Th>
                {TIERS.map((tier) => (
                  <Th key={tier.id} align="center">
                    {tier.name}
                  </Th>
                ))}
              </>
            }
          >
            {MATRIX.map((row) => (
              <tr key={row.feature}>
                <Td scope="row">
                  <span className="matrix__feature">{row.feature}</span>
                  {row.note && <span className="matrix__note">{row.note}</span>}
                </Td>
                {(["pilot", "crew", "enterprise"] as const).map((key) => (
                  <Td key={key} align="center">
                    {/* Icon plus a screen-reader word, never the icon alone:
                        a tick and a dash are indistinguishable to anyone
                        listening to this table. */}
                    {row[key] ? (
                      <>
                        <Check aria-hidden className="matrix__mark" />
                        <span className="sr-only">Included</span>
                      </>
                    ) : (
                      <>
                        <Minus aria-hidden className="matrix__mark matrix__mark--absent" />
                        <span className="sr-only">Not included</span>
                      </>
                    )}
                  </Td>
                ))}
              </tr>
            ))}
          </Table>
        </Reveal>
      </Section>

      {/* ── Questions ─────────────────────────────────────────────────────── */}
      <Section>
        <div className="split split--wide-right">
          <SectionIntro eyebrow="Questions" title="Asked on every first call" />

          <Reveal delay={80}>
            <dl className="def-list">
              {FAQ.map(({ q, a }) => (
                <div key={q} className="def-list__item">
                  <dt className="blurb__title">{q}</dt>
                  <dd className="blurb__body">{a}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </Section>

      <CtaBand
        title="Start on one site. Decide later."
        lead="The pilot tier is free and provisions itself. Nobody has to approve anything for you to see whether your crews will use it."
      />
    </>
  );
}
